// Контракт API пульта (задача D1): вход, сессия и ошибки входа.
//
// `POST /api/v1/auth/login` и `GET /api/v1/auth/me` отвечают `Me`; `POST /api/v1/auth/logout` — `{ ok: true }`.
// Коды и тексты ошибок входа отдаёт сервер (A13, `src/console/errors.ts`); здесь — их копия для пульта, совпадение
// держит `tests/console/contract.test.ts`.
import { z } from 'zod';
import { MomentSchema, RoleSchema } from './common.js';

/** Кто вошёл и до какого момента действует сессия (12 ч от входа, без продления). */
export const MeSchema = z.object({
  user: z.object({
    id: z.string().min(1),
    /** Полное имя, как в списке пульта: «Влад Тестов». */
    name: z.string(),
    /** Короткое имя для шапки и сайдбара: «Влад Т.». */
    shortName: z.string(),
    role: RoleSchema,
  }),
  expiresAt: MomentSchema,
});
export type Me = z.infer<typeof MeSchema>;

/** Форма входа: почта и пароль Омнидеска. Сервер принимает и почту без проверки вида — её проверяет Омнидеск. */
export const LoginBodySchema = z.object({ email: z.email(), password: z.string().min(1) });
export type LoginBody = z.infer<typeof LoginBodySchema>;

/** Ответ выхода. */
export const LogoutResponseSchema = z.object({ ok: z.literal(true) });

/** Коды ошибок входа и сессии и их HTTP-статусы (A13). */
export const AUTH_ERROR_STATUS = {
  bad_credentials: 401,
  no_access: 403,
  too_many_attempts: 429,
  omni_unavailable: 502,
  session_expired: 401,
  forbidden: 403,
  internal: 500,
  /** Настройки в базе не проходят проверку (`SettingsValidationError`, `kind: 'stored'`). */
  settings_unreadable: 500,
  /** Новое значение настроек неверно (`kind: 'patch'`): текст — замечания A9 с путём настройки, у каждого свой. */
  bad_settings: 400,
} as const;
export type AuthErrorCode = keyof typeof AUTH_ERROR_STATUS;

/** Постоянные тексты ошибок (A13 дословно). У `bad_settings` постоянного текста нет. */
export const AUTH_ERRORS_RU: Readonly<Record<Exclude<AuthErrorCode, 'bad_settings'>, string>> = {
  bad_credentials: 'Почта или пароль не подошли. Это те же данные, что для входа в Омнидеск.',
  no_access: 'Нет доступа к пульту: попросите Влада добавить вас',
  too_many_attempts: 'Слишком много попыток входа. Подождите минуту и попробуйте снова.',
  omni_unavailable: 'Омнидеск не отвечает, поэтому войти сейчас нельзя. Попробуйте через минуту.',
  session_expired: 'Сессия истекла: войдите снова.',
  forbidden: 'Нет прав на это действие: оно доступно роли «Админ».',
  internal: 'Внутренняя ошибка сервиса Анны. Подробности — в журнале ai-qual-api.',
  settings_unreadable: 'Настройки Анны в базе не проходят проверку. Откройте «Готовность» → «Настройки читаются».',
};
